import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Filter,
  Download,
  Calendar,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import { apiGet } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { API_ROUTES } from '@avgrp/shared';

// ─── Types ──────────────────────────────────────────────────

interface CostEntry {
  id: string;
  date: string;
  category: string;
  description: string;
  amount: number;
  flightNumber: string | null;
  createdBy: string;
}

interface CostSummary {
  totalCost: number;
  previousPeriodCost: number;
  changePercent: number;
  byCategory: { category: string; amount: number }[];
  byDay: { date: string; amount: number }[];
}

// ─── Default empty summary ──────────────────────────────────

const emptySummary: CostSummary = {
  totalCost: 0,
  previousPeriodCost: 0,
  changePercent: 0,
  byCategory: [],
  byDay: [],
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'];

// ─── Component ──────────────────────────────────────────────

export function CostsPage() {
  const { user } = useAuth();
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [categoryFilter, setCategoryFilter] = useState('');

  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['costs', 'summary', dateFrom, dateTo],
    queryFn: () =>
      apiGet<{ data: CostSummary }>(`${API_ROUTES.COSTS}/summary`, {
        params: { baseId: user?.baseId, startDate: dateFrom, endDate: dateTo },
      }),
    select: (res) => res.data,
  });

  const { data: entries, isLoading: isEntriesLoading } = useQuery({
    queryKey: ['costs', 'entries', dateFrom, dateTo, categoryFilter],
    queryFn: () =>
      apiGet<{ data: CostEntry[] }>(API_ROUTES.COSTS, {
        params: { baseId: user?.baseId, startDate: dateFrom, endDate: dateTo, category: categoryFilter || undefined },
      }),
    select: (res) => res.data,
  });

  const costSummary = summary ?? emptySummary;
  const costEntries = entries ?? [];
  const isPositiveChange = costSummary.changePercent > 0;
  const isLoading = isSummaryLoading || isEntriesLoading;

  const categories = [...new Set(costEntries.map((e) => e.category))];

  const filteredEntries = categoryFilter
    ? costEntries.filter((e) => e.category === categoryFilter)
    : costEntries;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cost Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and analyze operational costs
          </p>
        </div>
        <button
          className="btn-secondary"
          disabled={!filteredEntries.length}
          onClick={() => {
            const header = 'Date,Category,Description,Flight,Created By,Amount\n';
            const rows = filteredEntries
              .map((e) =>
                [
                  dayjs(e.date).format('YYYY-MM-DD'),
                  e.category,
                  `"${e.description.replace(/"/g, '""')}"`,
                  e.flightNumber || '',
                  e.createdBy,
                  e.amount,
                ].join(',')
              )
              .join('\n');
            const blob = new Blob([header + rows], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `costs_${dateFrom}_${dateTo}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <Download className="mr-2 h-4 w-4" />
          Export Report
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Cost (Period)</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    EUR {costSummary.totalCost.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl bg-aviation-50 p-3">
                  <DollarSign className="h-6 w-6 text-aviation-600" />
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Previous Period</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">
                    EUR {costSummary.previousPeriodCost.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-100 p-3">
                  <Calendar className="h-6 w-6 text-gray-500" />
                </div>
              </div>
            </div>
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Change</p>
                  <div className="mt-1 flex items-center gap-2">
                    <p className={`text-3xl font-bold ${isPositiveChange ? 'text-red-600' : 'text-emerald-600'}`}>
                      {isPositiveChange ? '+' : ''}{costSummary.changePercent.toFixed(1)}%
                    </p>
                  </div>
                </div>
                <div className={`rounded-xl p-3 ${isPositiveChange ? 'bg-red-50' : 'bg-emerald-50'}`}>
                  {isPositiveChange ? (
                    <TrendingUp className="h-6 w-6 text-red-500" />
                  ) : (
                    <TrendingDown className="h-6 w-6 text-emerald-500" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          {costSummary.byDay.length > 0 && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Daily costs bar chart */}
              <div className="card col-span-1 lg:col-span-2">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Daily Costs</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={costSummary.byDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(val: string) => dayjs(val).format('DD MMM')}
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                    />
                    <YAxis
                      tickFormatter={(val: number) => `${(val / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                    />
                    <Tooltip
                      formatter={(value: number) => [`EUR ${value.toLocaleString()}`, 'Cost']}
                      labelFormatter={(label: string) => dayjs(label).format('ddd, DD MMM YYYY')}
                    />
                    <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Category pie chart */}
              {costSummary.byCategory.length > 0 && (
                <div className="card col-span-1">
                  <h2 className="mb-4 text-lg font-semibold text-gray-900">By Category</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={costSummary.byCategory}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="amount"
                        nameKey="category"
                      >
                        {costSummary.byCategory.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => `EUR ${value.toLocaleString()}`} />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        iconSize={8}
                        formatter={(value: string) => (
                          <span className="text-xs text-gray-600">{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Filters + Table */}
          <div className="card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Cost Entries</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="cost-from" className="text-sm text-gray-500">From</label>
                  <input
                    id="cost-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="cost-to" className="text-sm text-gray-500">To</label>
                  <input
                    id="cost-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="input-field"
                  />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="input-field w-auto"
                >
                  <option value="">All categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header">Date</th>
                    <th className="table-header">Category</th>
                    <th className="table-header">Description</th>
                    <th className="table-header">Flight</th>
                    <th className="table-header">Created By</th>
                    <th className="table-header text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="table-cell text-gray-500">
                        {dayjs(entry.date).format('DD MMM YYYY')}
                      </td>
                      <td className="table-cell">
                        <span className="badge bg-gray-100 text-gray-700">{entry.category}</span>
                      </td>
                      <td className="table-cell">{entry.description}</td>
                      <td className="table-cell font-mono text-gray-500">
                        {entry.flightNumber || '-'}
                      </td>
                      <td className="table-cell text-gray-500">{entry.createdBy}</td>
                      <td className="table-cell text-right font-mono font-medium">
                        EUR {entry.amount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {filteredEntries.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-sm text-gray-400">
                        No cost entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
